import { useContext, useState } from "react";
import { VerityApiContext } from "../../api/fetch";
import { CoreModal, coreToast } from "../../core";
import { useMobileLayout } from "../../hooks";
import { VerityInstituteSettings } from "../..";
import ViewProfile from "./viewProfile";
import {
	ProctoringOption,
	ProctoringOptionProfileV2,
	OptionRelatedInstituteSettings,
} from "@verity/proctoring-option/types";
import { isInstituteProfile } from "@verity/proctoring-option";

const SaveProfile = ({
	activeProfileId,
	setActiveProfileId,
	refreshProfiles,
	profiles,
	setProctoringOption,
	proctoringOption,
	open,
	onClose,
	lmsCourseId,
	hasInstituteProfilePermissions,
	setIsUpdateCurrentProfileClicked,
	optionRelatedInstituteSettings,
	verityInstituteSettings,
}: {
	activeProfileId: number | undefined;
	setActiveProfileId: (id: number | undefined) => void;
	refreshProfiles: () => Promise<void | Map<number, ProctoringOptionProfileV2>>;
	profiles: Array<ProctoringOptionProfileV2>;
	proctoringOption: Partial<ProctoringOption>;
	open: boolean;
	onClose: () => void;
	lmsCourseId?: string;
	hasInstituteProfilePermissions: boolean;
	setIsUpdateCurrentProfileClicked: React.Dispatch<React.SetStateAction<boolean>>;
	setProctoringOption?: (options: Partial<ProctoringOption>) => void;
	optionRelatedInstituteSettings?: OptionRelatedInstituteSettings;
	verityInstituteSettings?: VerityInstituteSettings;
}) => {
	const { updateProctoringProfile } = useContext(VerityApiContext);
	const [createProfileModalShowing, setCreateProfileModalShowing] = useState(false);
	const isMobileLayout = useMobileLayout();

	const shouldShowCreateProfile = () => {
		const activeProfile = activeProfileId ? profiles.find((profile) => profile.id === activeProfileId) : undefined;
		// if institute profile but not admin, only allow save new profile (no edit)
		if (activeProfile && isInstituteProfile(activeProfile) && !hasInstituteProfilePermissions) {
			return true;
		}
		return !activeProfileId || activeProfileId <= 0 || createProfileModalShowing;
	};

	const getSelectedProfileName = () => {
		const activeProfile = profiles.find((profile) => profile.id === activeProfileId);
		return activeProfile?.name;
	};

	return (
		<>
			{shouldShowCreateProfile() ? (
				<ViewProfile
					title={"Save as New Proctoring Profile"}
					description={"You can save your proctoring settings as a profile to apply them in future assessments."}
					profileTitle=""
					setActiveProfileId={setActiveProfileId}
					refreshProfiles={refreshProfiles}
					profiles={profiles}
					open={open}
					onClose={onClose}
					proctoringOption={proctoringOption}
					viewOnly={false}
					lmsCourseId={lmsCourseId}
					hasInstituteProfilePermissions={hasInstituteProfilePermissions}
					setProctoringOption={setProctoringOption}
					optionRelatedInstituteSettings={optionRelatedInstituteSettings}
					verityInstituteSettings={verityInstituteSettings}
				/>
			) : (
				<CoreModal
					title={"Save as Proctoring Profile"}
					open={open}
					onClose={onClose}
					minWidth={isMobileLayout ? "100%" : "min(60%, 600px)"}
					widthFixed
					buttons={[
						{
							text: "No",
							color: "secondary",
							onClick: onClose,
						},
						{
							text: "Create New Profile",
							color: "primary",
							onClick: () => {
								setCreateProfileModalShowing(true);
							},
						},
						{
							text: "Update Current Profile",
							color: "primary",
							onClick: () => {
								setIsUpdateCurrentProfileClicked(true);
								if (activeProfileId) {
									const profileName = getSelectedProfileName();
									updateProctoringProfile({
										id: activeProfileId,
										proctoringOption: proctoringOption,
										name: profileName?.trim(),
									})
										.then((updatedProfile) => {
											coreToast.success("The proctoring profile has been updated.");
											const profilesCopy = Array.from(profiles.filter((x) => x.id === updatedProfile.id));
											profilesCopy.unshift(updatedProfile);
											void refreshProfiles();
											setActiveProfileId(updatedProfile.id);
										})
										.catch(console.error)
										.finally(onClose);
								}
							},
							hide: shouldShowCreateProfile(),
						},
					]}
				>
					<div style={{ paddingBottom: "10px", fontSize: "16px" }}>
						Would you like to update current profile ({getSelectedProfileName()}) with these settings or create a new
						one?
					</div>
				</CoreModal>
			)}
			{createProfileModalShowing && (
				<ViewProfile
					title={"Save as New Proctoring Profile"}
					description={"You can save your proctoring settings as a profile to apply them in future assessments."}
					profileTitle=""
					setActiveProfileId={setActiveProfileId}
					refreshProfiles={refreshProfiles}
					profiles={profiles}
					open={open}
					onClose={onClose}
					proctoringOption={proctoringOption}
					viewOnly={false}
					lmsCourseId={lmsCourseId}
					hasInstituteProfilePermissions={hasInstituteProfilePermissions}
					setProctoringOption={setProctoringOption}
					optionRelatedInstituteSettings={optionRelatedInstituteSettings}
					verityInstituteSettings={verityInstituteSettings}
				/>
			)}
		</>
	);
};

export default SaveProfile;
